// src/pages/ResetPassword.js
//
// Chunk 9 (triage #8) — the page users land on from the password-reset email.
// ForgotPassword.js sends the link with redirectTo `${origin}/reset-password`.
// Supabase's detectSessionInUrl consumes the recovery token in the URL hash and
// establishes a temporary session (firing PASSWORD_RECOVERY), so updateUser can
// set the new password. Standalone auth page (not inside the app chrome).
//
import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  // Supabase processes the recovery token from the URL hash automatically; the
  // PASSWORD_RECOVERY event confirms the recovery session is active. We don't
  // gate the form on it — updateUser surfaces a clear error if the link expired.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setError('')
    })
    return () => sub?.subscription?.unsubscribe()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (newPassword !== confirmPassword) { setError('Passwords don’t match.'); return }
    setLoading(true)
    const { error: updErr } = await supabase.auth.updateUser({ password: newPassword })
    if (updErr) {
      setLoading(false)
      setError(updErr.message || 'This reset link is invalid or has expired. Request a new one.')
      return
    }
    // Sign out the temporary recovery session so the user lands on a clean
    // sign-in and authenticates with their new password.
    await supabase.auth.signOut()
    setLoading(false)
    setDone(true)
    setTimeout(() => navigate('/login'), 2000)
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoMerq}>Merq</span>
          <span style={styles.logoVault}>Vault</span>
        </div>

        {done ? (
          <div style={styles.successBox}>
            <div style={styles.successTitle}>Password updated</div>
            <p style={styles.successText}>Redirecting you to sign in…</p>
            <Link to="/login" style={styles.backLink}>Go to sign in</Link>
          </div>
        ) : (
          <>
            <h2 style={styles.title}>Set a new password</h2>
            <p style={styles.subtitle}>Choose a new password for your account.</p>
            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label}>New password</label>
                <input
                  style={styles.input}
                  type="password"
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Confirm new password</label>
                <input
                  style={styles.input}
                  type="password"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <button
                style={{
                  ...styles.button,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                type="submit"
                disabled={loading}
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
            <div style={styles.back}>
              <Link to="/login" style={styles.link}>Cancel</Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const c = theme.colors
const f = theme.fonts

const styles = {
  container: {
    minHeight: '100vh',
    background: c.cream,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    fontFamily: f.body,
  },
  card: {
    background: c.white,
    borderRadius: '20px',
    padding: '40px 32px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 14px 50px rgba(30, 16, 64, 0.1)',
    fontFamily: f.body,
  },
  logo: {
    fontSize: '28px', fontWeight: 700,
    marginBottom: '24px',
    display: 'flex', alignItems: 'baseline',
    justifyContent: 'center', gap: '6px',
    letterSpacing: '-0.01em',
  },
  logoMerq: { color: c.plum },
  logoVault: { color: c.green, fontFamily: f.display, fontStyle: 'italic' },
  title: {
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '8px', textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px', color: c.inkMuted,
    marginBottom: '24px', textAlign: 'center',
  },
  field: { marginBottom: '14px' },
  label: {
    display: 'block',
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14.5px',
    outline: 'none', boxSizing: 'border-box',
    color: c.plum,
  },
  error: {
    background: c.roseSoft, color: c.rose,
    padding: '10px 12px', borderRadius: '8px',
    fontSize: '13px', marginBottom: '14px',
  },
  button: {
    width: '100%',
    padding: '13px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '12px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
  },
  back: { textAlign: 'center', marginTop: '16px' },
  link: {
    color: c.green, textDecoration: 'none',
    fontSize: '13px', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  successBox: { textAlign: 'center', padding: '12px 0' },
  successTitle: {
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '10px',
  },
  successText: {
    fontSize: '14px', color: c.inkMuted,
    marginBottom: '20px', lineHeight: 1.55,
  },
  backLink: {
    color: c.green, textDecoration: 'none',
    fontSize: '14px', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
}
